import { createTheme, ThemeOptions } from '@mui/material/styles';
import { darkTokens, lightTokens } from './tokens';

declare module '@mui/material/styles' {
  interface Palette {
    brand: {
      navy: string;
      yellow: string;
      accent: string;
      accentContrast: string;
    };
    border: string;
    raised: string;
    mutedText: string;
    danger: Palette['error'];
    alert: {
      critical: { bg: string; text: string; border: string };
      attention: { bg: string; text: string; border: string };
      caution: { bg: string; text: string; border: string };
      info: { bg: string; text: string; border: string };
      success: { bg: string; text: string; border: string };
    };
  }
  interface PaletteOptions {
    brand?: {
      navy?: string;
      yellow?: string;
      accent?: string;
      accentContrast?: string;
    };
    border?: string;
    raised?: string;
    mutedText?: string;
    danger?: PaletteOptions['error'];
    alert?: Palette['alert'];
  }
}

export function createDealSkoutTheme(mode: 'light' | 'dark') {
  const t = mode === 'dark' ? darkTokens : lightTokens;

  const options: ThemeOptions = {
    palette: {
      mode,
      primary: {
        main: t.brand.navy,
        dark: mode === 'dark' ? t.brand.navySoft : t.brand.navy,
        light: t.brand.navySoft,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: t.brand.yellowDark,
        contrastText: t.brand.navy,
      },
      background: {
        default: t.background.default,
        paper: t.background.paper,
      },
      text: {
        primary: t.text.primary,
        secondary: t.text.secondary,
      },
      divider: t.border,
      success: t.success,
      warning: t.warning,
      error: t.danger,
      info: t.info,
      brand: {
        navy: t.brand.navy,
        yellow: t.brand.yellow,
        accent: t.accent,
        accentContrast: t.accentContrast,
      },
      border: t.border,
      raised: t.background.raised,
      mutedText: t.text.muted,
      danger: t.danger,
      alert: t.alert,
    },
    typography: {
      fontFamily: '"Source Sans 3", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      h3: { fontWeight: 800, letterSpacing: '-0.02em' },
      h4: { fontWeight: 800 },
      h6: { fontWeight: 700 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    shape: { borderRadius: 8 },
    transitions: {
      duration: {
        shortest: 100,
        shorter: 150,
        short: 200,
        standard: 220,
        complex: 300,
        enteringScreen: 200,
        leavingScreen: 160,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
            },
          },
          body: {
            backgroundColor: t.background.default,
            color: t.text.primary,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 8,
            '&:focus-visible': {
              outline: `2px solid ${t.brand.yellow}`,
              outlineOffset: 2,
            },
          },
          containedPrimary: {
            backgroundColor: t.brand.navy,
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: t.brand.navyMid,
              boxShadow: 'inset 0 -3px 0 #F5C518',
            },
            '&.Mui-disabled': {
              backgroundColor: mode === 'dark' ? '#1A3354' : '#D5DCE6',
              color: t.text.muted,
            },
          },
          outlinedPrimary: {
            borderColor: mode === 'dark' ? t.text.secondary : t.brand.navy,
            color: mode === 'dark' ? t.text.primary : t.brand.navy,
            '&:hover': {
              borderWidth: 2,
              borderColor: mode === 'dark' ? t.text.primary : t.brand.navy,
              backgroundColor: mode === 'dark' ? 'rgba(242,245,249,0.08)' : 'rgba(11,31,58,0.06)',
            },
            '&.Mui-disabled': {
              color: t.text.muted,
              borderColor: t.border,
            },
          },
          text: {
            color: t.text.primary,
            '&:hover': {
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
          textPrimary: {
            color: mode === 'dark' ? t.text.primary : t.brand.navy,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: t.text.primary,
            '&:focus-visible': {
              outline: `2px solid ${t.brand.yellow}`,
              outlineOffset: 2,
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
          colorPrimary: {
            color: mode === 'dark' ? t.text.primary : t.brand.navy,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            color: t.text.primary,
            borderColor: t.border,
            '&:focus-visible': {
              outline: `2px solid ${t.brand.yellow}`,
              outlineOffset: 2,
            },
            '&.Mui-selected': {
              color: t.brand.navy,
              backgroundColor: t.brand.yellow,
              fontWeight: 800,
              '&:hover': { backgroundColor: t.brand.yellow },
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: t.background.paper,
            color: t.text.primary,
            borderBottom: `1px solid ${t.border}`,
            boxShadow: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            color: t.text.secondary,
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(245,197,24,0.12)' : 'rgba(11,31,58,0.06)',
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
            },
            '&:focus-visible': {
              outline: `2px solid ${t.brand.yellow}`,
              outlineOffset: 2,
            },
            '&.Mui-selected': {
              color: t.text.primary,
              fontWeight: 800,
              backgroundColor: mode === 'dark' ? 'rgba(245,197,24,0.16)' : 'rgba(245,197,24,0.35)',
              boxShadow: `inset 0 -3px 0 ${t.brand.yellow}`,
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: t.brand.yellow,
            height: 3,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: t.text.primary,
            fontWeight: 700,
            textDecorationColor: t.brand.yellow,
            textUnderlineOffset: '2px',
            '&:hover': { color: t.text.primary },
            '&:focus-visible': {
              outline: `2px solid ${t.brand.yellow}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ ownerState }: { ownerState?: { color?: string } }) =>
            mode === 'dark' && ownerState?.color === 'primary' ? { color: t.text.primary } : {},
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: t.text.secondary,
            '&.Mui-focused': {
              color: mode === 'dark' ? t.brand.yellow : t.brand.navy,
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: { color: t.text.primary },
          input: {
            '&::placeholder, &::-webkit-input-placeholder': {
              color: t.text.secondary,
              opacity: 1,
            },
            '&:focus::placeholder, &:focus::-webkit-input-placeholder': {
              color: t.text.secondary,
              opacity: 1,
            },
            '&.Mui-disabled': {
              color: t.text.muted,
              WebkitTextFillColor: t.text.muted,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.text.secondary },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? t.brand.yellow : t.brand.navy,
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: t.text.secondary,
            '&.Mui-checked': {
              color: mode === 'dark' ? t.brand.yellow : t.brand.navy,
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: t.text.secondary,
            '&.Mui-checked': {
              color: mode === 'dark' ? t.brand.yellow : t.brand.navy,
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': {
              color: mode === 'dark' ? t.brand.yellow : t.brand.navy,
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standardError: {
            backgroundColor: t.alert.critical.bg,
            color: t.alert.critical.text,
            border: `1px solid ${t.alert.critical.border}`,
            '& .MuiAlert-icon': { color: t.alert.critical.border },
          },
          standardWarning: {
            backgroundColor: t.alert.attention.bg,
            color: t.alert.attention.text,
            border: `1px solid ${t.alert.attention.border}`,
            '& .MuiAlert-icon': { color: t.alert.attention.border },
          },
          standardInfo: {
            backgroundColor: t.alert.info.bg,
            color: t.alert.info.text,
            border: `1px solid ${t.alert.info.border}`,
            '& .MuiAlert-icon': { color: t.alert.info.border },
          },
          standardSuccess: {
            backgroundColor: t.alert.success.bg,
            color: t.alert.success.text,
            border: `1px solid ${t.alert.success.border}`,
            '& .MuiAlert-icon': { color: t.alert.success.border },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 700 },
          outlined: {
            borderColor: t.border,
            color: t.text.primary,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: t.background.raised,
            color: t.text.primary,
            border: `1px solid ${t.border}`,
            fontSize: '0.8125rem',
          },
        },
      },
    },
  };

  return createTheme(options);
}
